import {
  Alert,
  Autocomplete,
  Box,
  MenuItem,
  Paper,
  TextField,
} from '@mui/material';
import {
  ExtractionTypeOption,
  PageSize,
  PracticeOption,
  QueueSort,
  ReviewStatusFilter,
  useAppSelector,
  useGetReviewFilterOptionsQuery,
} from 'api';

const REVIEW_STATUS_OPTIONS: { value: ReviewStatusFilter; label: string }[] = [
  { value: 'All', label: 'All' },
  { value: 'NeedsReview', label: 'Needs review' },
  { value: 'Reviewed', label: 'Reviewed' },
];

const SORT_OPTIONS: { value: QueueSort; label: string }[] = [
  { value: 'dateDesc', label: 'Date, newest first' },
  { value: 'dateAsc', label: 'Date, oldest first' },
  { value: 'confidenceAsc', label: 'Confidence, lowest first' },
  { value: 'confidenceDesc', label: 'Confidence, highest first' },
];

const PAGE_SIZE_OPTIONS: PageSize[] = [25, 50, 100];

const ALL_TYPES: ExtractionTypeOption = {
  extractionTypeKey: -1,
  extractionDescription: 'All',
  enabledFlag: true,
};
const ALL_PRACTICES: PracticeOption = { clientKey: '', clientName: 'All' };

type QueueFiltersProps = {
  reviewStatus: ReviewStatusFilter;
  onReviewStatusChange: (value: ReviewStatusFilter) => void;
  extractionTypeKey: number | null;
  onExtractionTypeKeyChange: (value: number | null) => void;
  mrnInput: string;
  onMrnInputChange: (value: string) => void;
  clientKey: string | null;
  onClientKeyChange: (value: string | null) => void;
  sort: QueueSort;
  onSortChange: (value: QueueSort) => void;
  pageSize: PageSize;
  onPageSizeChange: (value: PageSize) => void;
};

export const QueueFilters = ({
  reviewStatus,
  onReviewStatusChange,
  extractionTypeKey,
  onExtractionTypeKeyChange,
  mrnInput,
  onMrnInputChange,
  clientKey,
  onClientKeyChange,
  sort,
  onSortChange,
  pageSize,
  onPageSizeChange,
}: QueueFiltersProps) => {
  const { mockUser } = useAppSelector(state => state.appUser);
  const uid = mockUser?.id.toString() ?? '';
  const {
    data: options,
    isLoading: optionsLoading,
    isError: optionsError,
  } = useGetReviewFilterOptionsQuery({ uid }, { skip: !uid });

  const selectedType =
    extractionTypeKey == null
      ? ALL_TYPES
      : options?.extractionTypes.find(
          t => t.extractionTypeKey === extractionTypeKey
        ) ?? ALL_TYPES;

  const selectedPractice =
    clientKey == null
      ? ALL_PRACTICES
      : options?.practices.find(p => p.clientKey === clientKey) ??
        ALL_PRACTICES;

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      {optionsError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Filter options could not be loaded. Status, patient, and sort
          filters still work.
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
          alignItems: 'flex-start',
        }}
      >
        <TextField
          select
          label="Review status"
          size="small"
          value={reviewStatus}
          onChange={e =>
            onReviewStatusChange(e.target.value as ReviewStatusFilter)
          }
        >
          {REVIEW_STATUS_OPTIONS.map(opt => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>

        <Autocomplete
          size="small"
          options={[ALL_TYPES, ...(options?.extractionTypes ?? [])]}
          getOptionLabel={o => o.extractionDescription}
          isOptionEqualToValue={(o, v) =>
            o.extractionTypeKey === v.extractionTypeKey
          }
          value={selectedType}
          loading={optionsLoading}
          onChange={(_, v) =>
            onExtractionTypeKeyChange(
              v && v.extractionTypeKey !== -1 ? v.extractionTypeKey : null
            )
          }
          renderInput={params => (
            <TextField {...params} label="Extraction type" />
          )}
        />

        <TextField
          label="MRN"
          size="small"
          value={mrnInput}
          onChange={e => onMrnInputChange(e.target.value)}
          placeholder="All patients"
        />

        <Autocomplete
          size="small"
          options={[ALL_PRACTICES, ...(options?.practices ?? [])]}
          getOptionLabel={o => o.clientName}
          isOptionEqualToValue={(o, v) => o.clientKey === v.clientKey}
          value={selectedPractice}
          loading={optionsLoading}
          onChange={(_, v) =>
            onClientKeyChange(v && v.clientKey !== '' ? v.clientKey : null)
          }
          renderInput={params => <TextField {...params} label="Practice" />}
        />

        <TextField
          select
          label="Sort"
          size="small"
          value={sort}
          onChange={e => onSortChange(e.target.value as QueueSort)}
        >
          {SORT_OPTIONS.map(opt => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          label="Page size"
          size="small"
          value={pageSize}
          onChange={e => onPageSizeChange(Number(e.target.value) as PageSize)}
        >
          {PAGE_SIZE_OPTIONS.map(size => (
            <MenuItem key={size} value={size}>
              {size}
            </MenuItem>
          ))}
        </TextField>
      </Box>
    </Paper>
  );
};
